/**
 * Catching `beforeinstallprompt`, which arrives whether or not React is ready.
 *
 * The event fires ONCE, early, on Chromium's own schedule — and if nothing
 * calls `preventDefault()` on it synchronously, the browser shows its own mini
 * infobar and the app loses the chance to ask properly. A listener attached
 * inside a component's effect is attached too late on a slow phone, which is
 * exactly the device that matters here.
 *
 * So the listener lives at module scope and is registered from `main.tsx`
 * before the first render. The event is parked here; React subscribes to it.
 */

/** Not in lib.dom yet — Chromium-only, and still unspecified. */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

let parked: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
const listeners = new Set<() => void>();

const announce = () => {
  for (const l of listeners) l();
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppresses Chromium's own infobar. The app asks in its own words, at a
    // moment it chose, instead of competing with a strip at the bottom.
    e.preventDefault();
    parked = e as BeforeInstallPromptEvent;
    announce();
  });

  /**
   * Fires however the install happened — our button, the address-bar icon, the
   * browser menu. It is the only reliable "they did it" signal on Chromium, and
   * it is why the app does not have to trust its own button's return value.
   */
  window.addEventListener("appinstalled", () => {
    parked = null;
    installedThisSession = true;
    announce();
  });
}

export const installPrompt = {
  /** True when one tap can genuinely install the app right now. */
  ready: () => parked !== null,

  /** True once this tab has seen the app get installed. */
  justInstalled: () => installedThisSession,

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => void listeners.delete(fn);
  },

  /**
   * Shows the native dialog. Must be called from a user gesture — Chromium
   * rejects it otherwise, which is why nothing here fires it automatically.
   */
  async show(): Promise<InstallOutcome> {
    const event = parked;
    if (!event) return "unavailable";

    /**
     * Cleared BEFORE the await, not after.
     *
     * A deferred event is single-use: a second `prompt()` on the same one
     * throws. Between the tap and the user's answer there is a real window in
     * which a double tap — or an impatient second tap on a slow phone — would
     * re-enter this function, and clearing afterwards leaves that window open.
     */
    parked = null;
    announce();

    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  },
};
